import { Overlay } from '@angular/cdk/overlay';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CoreModule } from '@base/core.module';
import { AppState } from '@base/store';
import { CorePreloadActions } from '@base/store/preload';
import { CoreUiSelectors } from '@base/store/ui';
import { ThemeModule } from '@modules/theme/theme.module';
import { Store } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  AngularMaterialModule,
  MAT_DIALOG_DEFAULT_OPTIONS_PROVIDER,
  MAT_SNACK_BAR_DEFAULT_OPTIONS_PROVIDER
} from '@shared/external-modules/angular-material.module';
import { BuiSpinnerModule } from '@shared/modules/bui/bui-spinner/bui-spinner.module';
import { filter, take } from 'rxjs/operators';
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';
import { DEFAULT_LOCALE_ID } from '@shared/util/util';
import localeSrLatn from '@angular/common/locales/sr-Latn';
import { registerLocaleData } from '@angular/common';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { ParametersRestService } from '@base/modules/rest/parameters/parameters-rest.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { SnackBarModule } from '@shared/components/snack-bar/snack-bar.module';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { initializer } from './keycloak-configuration';
import { TranslationLoaderService } from '@base/services/translation-loader.service';
import { AppConfiguration } from './app-configuration';

registerLocaleData(localeSrLatn);

// AoT requires an exported function for factories
export function createTranslationLoader(httpClient: HttpClient, appConfiguration: AppConfiguration) {
  return new TranslationLoaderService(httpClient, appConfiguration);
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    AngularMaterialModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    BuiSpinnerModule,
    CoreModule,
    HttpClientModule,
    MatIconModule,
    ThemeModule,
    SnackBarModule,
    HighlightModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslationLoader,
        deps: [HttpClient, AppConfiguration],
      },
      useDefaultLang: true,
    }),
    KeycloakAngularModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializer,
      deps: [ParametersRestService, KeycloakService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (store: Store<AppState>) => {
        return () => new Promise(resolve => {

          // Dispatch loading action
          store.dispatch(CorePreloadActions.LoadAppPublicData());

          // Wait until loading finish
          store.select(CoreUiSelectors.loadingData)
            .pipe(filter((loadingData: boolean) => !loadingData))
            .pipe(take(1))
            .subscribe(() => {
              resolve(true);
            });
        });
      },
      multi: true,
      deps: [Store],
    },
    {
      provide: LOCALE_ID,
      useValue: DEFAULT_LOCALE_ID,
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        fullLibraryLoader: () => import('highlight.js'),
      },
    },
    MAT_DIALOG_DEFAULT_OPTIONS_PROVIDER,
    MAT_SNACK_BAR_DEFAULT_OPTIONS_PROVIDER,
    MatSnackBar,
    Overlay,
  ],
  bootstrap: [
    AppComponent,
  ],
})
export class AppModule {
  constructor(
    private iconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer
  ) {
    iconRegistry.addSvgIconSetInNamespace(
      'mdi',
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/images/mdi.svg')
    );
    iconRegistry.addSvgIconSetInNamespace(
      'mat',
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/images/mat.svg')
    );
    iconRegistry.addSvgIconSetInNamespace(
      'svg',
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/images/symbol-defs.svg')
    );
  }
}
